import React, { useCallback, useEffect, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { PageLoadError } from "./NoAccess";
import { useScreenWrites } from "./screenWrites";
import { Loader2, Plus, Trash2, TrendingUp, TrendingDown, AlertTriangle, Calendar } from "lucide-react";
import CurrencyPicker from "./charts/CurrencyPicker";
import { currenciesOf } from "./charts/currencies";
import { CHART_GRID, CHART_TICK, CHART_TOOLTIP, CHART_LEGEND, CHART_SERIES } from "./charts/chartTheme";
import { formatCompactMoney } from "./charts/chartFormat";
import { formatMoneyLedger } from "../lib/formatMoney";
import { formatDate } from "../lib/dateTime";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";

const TABS = ["pl", "ar", "expenses", "forecast"];

const EXPENSE_CATEGORIES = ["marketing", "salaries", "infra", "tools", "legal", "office", "other"];
const CAT_COLORS = ["#E6B85C", "#d97757", "#7FB8A4", "#a78bfa", "#f87171", "#60a5fa", "#fbbf24"];

export default function HqFinance() {
  const t = useT("superadmin");
  const [tab, setTab] = useState("pl");
  return (
    <div data-testid="hq-finance">
      <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("hf.title")}</h1>
      <p className="text-slate-500 mb-6">{t("hf.subtitle")}</p>

      <div className="flex gap-2 mb-6">
        {TABS.map((tabKey) => (
          <button key={tabKey} onClick={() => setTab(tabKey)} data-testid={`fin-tab-${tabKey}`}
            className={`px-4 py-2 rounded-xl text-sm transition ${tab === tabKey ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200 hover:bg-white shadow-sm"}`}>
            {t(`hf.tab.${tabKey}`)}
          </button>
        ))}
      </div>

      {tab === "pl" && <PnLTab t={t} />}
      {tab === "ar" && <ARagingTab t={t} />}
      {tab === "expenses" && <ExpensesTab t={t} />}
      {tab === "forecast" && <ForecastTab t={t} />}
    </div>
  );
}

/**
 * One tab's read: `{ data, error, reload }`. A failed read (a 403 for a role without
 * finance.read included) is reported through PageLoadError; it never leaves the tab spinning.
 * `params` is a flat object of query parameters.
 */
function useTabRead(path, params) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const paramsKey = JSON.stringify(params || {});
  useEffect(() => {
    let live = true;
    setError(null);
    api.get(path, { params: JSON.parse(paramsKey) })
      .then((r) => { if (live) setData(r.data); })
      .catch((e) => { if (live) setError(e); });
    return () => { live = false; };
  }, [path, paramsKey, attempt]);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { data, error, reload };
}

function PnLTab({ t }) {
  const { lang } = useLanguage();
  const { data, error, reload } = useTabRead("/super-admin/hq/finance/pl");
  const [picked, setPicked] = useState(null);
  if (error) return <PageLoadError error={error} onRetry={reload} testId="pl-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  // ONE CHART, ONE CURRENCY. Every figure the endpoint returns is a map of currency ->
  // amount (`shape: "by_currency"`), because a CAD month and a USD month do not add up.
  // The stats show every currency; the chart plots the one selected, defaulting to the
  // platform's own.
  const currencies = currenciesOf([data.total_revenue, data.total_expenses], data.settlement_currency);
  const shown = picked || data.settlement_currency || currencies[0];
  const series = (data.series || []).map((s) => ({
    month: s.month,
    revenue: (s.revenue || {})[shown] || 0,
    expenses: (s.expenses || {})[shown] || 0,
    net: (s.net || {})[shown] || 0,
  }));
  const net = data.net || {};
  return (
    <div data-testid="pl-tab">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <MoneyStat label={t("hf.totalRevenue")} amounts={data.total_revenue} color="emerald" icon={TrendingUp} testid="pl-total-revenue" />
        <MoneyStat label={t("hf.totalExpenses")} amounts={data.total_expenses} color="rose" icon={TrendingDown} testid="pl-total-expenses" />
        <MoneyStat label={t("hf.net")} amounts={net} color={(net[shown] || 0) >= 0 ? "amber" : "rose"} testid="pl-net" />
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-sans font-semibold text-lg" data-testid="pl-chart-title">{t("hf.revVsExp", { currency: String(shown || "").toUpperCase() })}</h3>
          <CurrencyPicker currencies={currencies} value={shown} onChange={setPicked} testid="pl" label={t("hf.currency")} />
        </div>
        {series.length === 0 ? <p className="text-slate-400 py-12 text-center text-sm">{t("hf.noHistory")}</p> : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={series}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="month" tick={CHART_TICK} />
              {/* The series are major units; the formatter takes cents. */}
              <YAxis tick={CHART_TICK} width={64} tickFormatter={(v) => formatCompactMoney(Math.round(v * 100), shown, lang)} />
              <Tooltip {...CHART_TOOLTIP} />
              <Legend {...CHART_LEGEND} />
              <Bar dataKey="revenue" fill={CHART_SERIES.positive} name={t("hf.legendRevenue")} />
              <Bar dataKey="expenses" fill={CHART_SERIES.negative} name={t("hf.legendExpenses")} />
              <Bar dataKey="net" fill={CHART_SERIES.revenue} name={t("hf.legendNet")} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function ARagingTab({ t }) {
  const { lang } = useLanguage();
  const { data, error, reload } = useTabRead("/super-admin/hq/finance/ar-aging");
  const [picked, setPicked] = useState(null);
  if (error) return <PageLoadError error={error} onRetry={reload} testId="ar-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  // ONE CURRENCY AT A TIME. An invoice in CAD is never aged into a USD bucket; the picker
  // chooses which currency's receivables the tiles and the table show.
  // `currenciesOf` falls back to the settlement currency for empty maps; an empty AR has
  // no currency to show, so the emptiness is read from the map itself.
  if (Object.keys(data.by_currency || {}).length === 0) {
    return (
      <div data-testid="ar-tab">
        <p className="text-xs text-slate-500 mb-3" data-testid="ar-explainer">{t("hf.arExplainer")}</p>
        <p className="text-slate-400 py-12 text-center text-sm">{t("hf.noOpenAr")}</p>
      </div>
    );
  }
  const currencies = currenciesOf([data.by_currency], data.settlement_currency);
  const shown = currencies.includes(picked) ? picked : currencies[0];
  const { buckets, total_cents: total, by_tenant: byTenant } = data.by_currency[shown];
  const bucketDefs = [
    { key: "current", label: t("hf.bucket.current"), color: "text-emerald-600" },
    { key: "1_30", label: t("hf.bucket.1_30"), color: "text-amber-600" },
    { key: "31_60", label: t("hf.bucket.31_60"), color: "text-orange-400" },
    { key: "61_90", label: t("hf.bucket.61_90"), color: "text-rose-400" },
    { key: "90_plus", label: t("hf.bucket.90_plus"), color: "text-rose-500" },
  ];
  return (
    <div data-testid="ar-tab">
      <div className="flex items-start justify-between gap-4 mb-3">
        <p className="text-xs text-slate-500" data-testid="ar-explainer">{t("hf.arExplainer")}</p>
        <CurrencyPicker currencies={currencies} value={shown} onChange={setPicked} testid="ar" label={t("hf.currency")} />
      </div>
      <div className="grid grid-cols-5 gap-3 mb-6">
        {bucketDefs.map((b) => (
          <div key={b.key} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`ar-bucket-${b.key}`}>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{b.label}</div>
            <div className={`font-sans font-semibold text-2xl ${b.color}`} data-testid={`ar-bucket-${b.key}-amount`}>{formatMoneyLedger(buckets[b.key] || 0, shown, lang)}</div>
          </div>
        ))}
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-sans font-semibold text-lg">{t("hf.arByTenant")}</h3>
          <span className="text-sm text-slate-400">{t("hf.totalDue")} <span className="text-amber-600 font-medium">{formatMoneyLedger(total, shown, lang)}</span></span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-400 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">{t("hf.colTenant")}</th><th className="text-left px-4 py-3">{t("hf.colInvoices")}</th><th className="text-left px-4 py-3">{t("hf.colMaxDelay")}</th><th className="text-right px-4 py-3">{t("hf.colAmount")}</th></tr>
          </thead>
          <tbody>
            {byTenant.map((r) => {
              // `practice_state` (D7 contract 4.4): `live` is a practice that exists; `deleted` one
              // deleted after it was billed (a deletion record covers the invoice; its invoices
              // are still owed, labelled by the name the record kept); `unknown` an address no
              // practice holds and no record covers. A deleted row is keyed by its identity
              // (`tenant_ref`), so two practices that held one address never share a key. Its
              // second line names the address it held and the day it was deleted: deleted
              // practices often share a name, and the label alone cannot tell them apart.
              const state = r.practice_state || "live";
              const rowId = state === "deleted" ? `${r.tenant_id}-deleted-${r.tenant_ref}`
                : state === "unknown" ? `${r.tenant_id}-unknown` : r.tenant_id;
              const label = state === "deleted" ? t("hf.arDeletedPractice", { name: r.tenant_name || r.tenant_id })
                : state === "unknown" ? t("hf.arUnknownPractice", { id: r.tenant_id }) : r.tenant_name;
              const identity = state !== "deleted" ? null
                : r.deleted_at ? t("hf.arDeletedIdentity", { address: r.tenant_id, date: formatDate(r.deleted_at, lang) })
                  : t("hf.arDeletedIdentityUndated", { address: r.tenant_id });
              return (
              <tr key={rowId} data-testid={`ar-row-${rowId}`} className="border-t border-slate-100">
                <td className="px-4 py-3">
                  <code className="text-amber-600 text-xs">{label}</code>
                  {identity && <div className="text-xs text-slate-400 mt-0.5" data-testid={`ar-row-${rowId}-identity`}>{identity}</div>}
                </td>
                <td className="px-4 py-3 text-slate-500">{r.count}</td>
                <td className="px-4 py-3">
                  <span className={r.max_days_overdue > 60 ? "text-rose-400" : r.max_days_overdue > 30 ? "text-amber-600" : "text-slate-500"}>
                    {r.max_days_overdue > 0 ? <><AlertTriangle className="w-3 h-3 inline mr-1" />{t("hf.daysOverdue", { n: r.max_days_overdue })}</> : "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-medium">{formatMoneyLedger(r.amount_cents, shown, lang)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ExpensesTab({ t }) {
  // Reading is finance.read; adding and deleting an expense are finance.write (not Read only).
  const { expenses: canWrite } = useScreenWrites("hqFinance");
  const [picked, setPicked] = useState(null);
  // NO `currency` IN THE FORM. The platform's own currency is the server's answer
  // (`gateway_settings.settlementCurrency`, applied by `create_expense`); a constant typed
  // here would silently label every expense with whatever this file last guessed.
  const [form, setForm] = useState({ amount: "", category: "marketing", date: new Date().toISOString().slice(0, 10), vendor: "", description: "" });
  const [creating, setCreating] = useState(false);

  const { data, error, reload: load } = useTabRead("/super-admin/hq/finance/expenses");
  if (error) return <PageLoadError error={error} onRetry={load} testId="expenses-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/super-admin/hq/finance/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success(t("hf.expenseSaved")); setCreating(false);
      setForm({ amount: "", category: "marketing", date: new Date().toISOString().slice(0, 10), vendor: "", description: "" });
      load();
    } catch (err) { toast.error(extractApiError(err, t("hf.error"), t)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("hf.confirmDeleteExpense"))) return;
    try {
      await api.delete(`/super-admin/hq/finance/expenses/${id}`);
    } catch (err) { toast.error(extractApiError(err, t("hf.error"), t)); return; }
    toast.success(t("hf.deleted")); load();
  };

  // A pie of two currencies is a pie of nothing: the slices are plotted for ONE, the
  // totals are stated for each.
  const currencies = currenciesOf([data.total], data.settlement_currency);
  const shown = picked || data.settlement_currency || currencies[0];
  const pieData = Object.entries(data.by_category || {})
    .map(([k, v]) => ({ name: k, value: (v || {})[shown] || 0 }))
    .filter((d) => d.value > 0);

  return (
    <div data-testid="expenses-tab">
      <div className="flex justify-between mb-6 items-start">
        <MoneyStat label={t("hf.expenses365")} amounts={data.total} color="rose" testid="exp-total" />
        <div className="flex items-center gap-3">
          <CurrencyPicker currencies={currencies} value={shown} onChange={setPicked} testid="exp" label={t("hf.currency")} />
          {canWrite && (
            <Button onClick={() => setCreating(true)} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="new-expense">
              <Plus className="w-4 h-4 mr-2" /> {t("hf.newExpense")}
            </Button>
          )}
        </div>
      </div>

      {canWrite && creating && (
        <form onSubmit={submit} className="rounded-2xl bg-sol-gold/5 border border-sol-gold/20 p-6 mb-6 grid md:grid-cols-3 gap-3" data-testid="new-expense-form">
          <Input required type="number" step="0.01" placeholder={t("hf.phAmount")} value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-white border-slate-200" data-testid="exp-amount" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm">
            {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c} className="bg-white">{c}</option>)}
          </select>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="bg-white border-slate-200" />
          <Input placeholder={t("hf.phVendor")} value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
            className="bg-white border-slate-200" />
          <Input placeholder={t("hf.phDescription")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="bg-white border-slate-200 md:col-span-2" />
          <Button type="submit" className="bg-sol-gold text-black md:col-span-3" data-testid="exp-submit">{t("hf.saveExpense")}</Button>
        </form>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <h3 className="font-sans font-semibold text-lg mb-4">{t("hf.byCategory")}</h3>
          {pieData.length === 0 ? <p className="text-slate-400 py-12 text-center text-sm">{t("hf.noExpense")}</p> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={90} label={(e) => `${e.name}: ${e.value.toFixed(0)}`}>
                  {pieData.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                </Pie>
                <Tooltip {...CHART_TOOLTIP} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-0 overflow-hidden">
          <h3 className="font-sans font-semibold text-lg p-6 pb-2">{t("hf.history")}</h3>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.items.map((e) => (
                  <tr key={e.expense_id} className="border-t border-slate-100" data-testid={`exp-${e.expense_id}`}>
                    <td className="px-4 py-3 text-xs text-slate-400">{e.date}</td>
                    <td className="px-4 py-3"><code className="text-xs text-amber-600">{e.category}</code></td>
                    <td className="px-4 py-3 text-slate-800/70 text-xs truncate max-w-xs">{e.vendor || e.description || "—"}</td>
                    <td className="px-4 py-3 text-right font-medium">{e.amount.toFixed(2)} {(e.currency || data.settlement_currency || "").toUpperCase()}</td>
                    <td className="px-4 py-3 text-right">
                      {canWrite && (
                        <button onClick={() => del(e.expense_id)} className="text-rose-400 hover:text-rose-600" data-testid={`del-exp-${e.expense_id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">{t("hf.noExpense")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ForecastTab({ t }) {
  const { lang } = useLanguage();
  const [horizon, setHorizon] = useState(6);
  const { data, error, reload } = useTabRead("/super-admin/hq/finance/forecast", { horizon_months: horizon });
  if (error) return <PageLoadError error={error} onRetry={reload} testId="forecast-load-error" />;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  // The projection is in ONE currency, the platform's (`currency`); both figures are amounts
  // in it. Revenue in any other currency is left out by the server and stated here, apart.
  const currency = data.currency || "";
  const growth = data.monthly_growth_avg || 0;
  const series = Array.isArray(data.series) ? data.series : [];
  const excluded = Object.entries(data.excluded_by_currency || {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div data-testid="forecast-tab">
      <div className="flex gap-2 mb-6">
        {[3, 6, 12].map((h) => (
          <button key={h} onClick={() => setHorizon(h)} className={`px-3 py-1.5 rounded-lg text-xs ${horizon === h ? "bg-sol-gold/15 text-amber-600 border border-sol-gold/30" : "bg-white shadow-sm text-slate-500 border border-slate-200"}`}>
            {t("hf.months", { n: h })}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <MoneyStat label={t("hf.baseCurrentMonth")} amounts={{ [currency]: data.base_last_month }} color="amber" icon={Calendar} testid="forecast-base" />
        <MoneyStat label={t("hf.avgMonthlyGrowth")} amounts={{ [currency]: growth }} color={growth >= 0 ? "emerald" : "rose"} icon={growth >= 0 ? TrendingUp : TrendingDown} testid="forecast-growth" />
      </div>
      {excluded.length > 0 && (
        <p className="text-xs text-slate-500 mb-4" data-testid="forecast-excluded">
          {t("hf.forecastExcluded", { amounts: excluded.map(([cur, amount]) => `${Number(amount || 0).toFixed(2)} ${cur.toUpperCase()}`).join(", ") })}
        </p>
      )}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-sans font-semibold text-lg mb-4">{t("hf.projectionTitle", { n: horizon, currency: currency.toUpperCase() })}</h3>
        {series.length === 0 ? <p className="text-slate-400 py-12 text-center text-sm">{t("hf.notEnoughHistory")}</p> : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid {...CHART_GRID} />
              <XAxis dataKey="month" tick={CHART_TICK} />
              <YAxis tick={CHART_TICK} width={64} tickFormatter={(v) => formatCompactMoney(Math.round(v * 100), currency, lang)} />
              <Tooltip {...CHART_TOOLTIP} />
              <Line type="monotone" dataKey="projected_revenue" stroke={CHART_SERIES.revenue} strokeWidth={2} dot={{ fill: CHART_SERIES.revenue }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

/**
 * A figure that is a MAP of currency -> amount, never a scalar.
 *
 * HQ finance reports what the platform itself earned and spent, and those arrive in more
 * than one currency. The endpoints therefore return one amount per currency and this
 * prints them as separate lines: a total that added them together would be true in no
 * currency at all, and the label under it used to read a hardcoded "CAD" whatever the
 * number above it was.
 */
function MoneyStat({ label, amounts, color = "amber", icon: Icon, testid }) {
  const colors = {
    emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600",
  };
  const rows = Object.entries(amounts || {}).sort(([a], [b]) => a.localeCompare(b));
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider mb-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      {rows.length === 0 && <div className={`font-sans font-semibold text-3xl ${colors[color]}`}>—</div>}
      {rows.map(([cur, amount]) => (
        <div key={cur} className="flex items-baseline gap-2">
          <span className={`font-sans font-semibold text-3xl ${colors[color]}`}>{Number(amount || 0).toFixed(2)}</span>
          <span className="text-xs text-slate-400 uppercase tracking-wider">{cur}</span>
        </div>
      ))}
    </div>
  );
}
